import { ThunkDispatch, EmptyObject, AnyAction } from '@reduxjs/toolkit';
import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import { Dispatch } from 'react';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { SalesIrregularitiesReceiptResponseType } from '../../../../services/Sales/types';
import { saleSlice } from '../../../../stores';
import { logoUrl } from '../../../../theme';
import { delay } from '../../../../utils/Helpers';

export const exportIrregularitiesReceiptToPDF = async (
  apiData: SalesIrregularitiesReceiptResponseType,
  dispatch: ThunkDispatch<EmptyObject & PersistPartial, undefined, AnyAction> &
    Dispatch<AnyAction>,
  worker: (
    apiData: SalesIrregularitiesReceiptResponseType,
    driverName: string,
  ) => Promise<
    {
      title: string;
      body: string;
    }[]
  >,
  driverName: string,
): Promise<void> => {
  dispatch(saleSlice.actions.setSaleIrregularitiesReceiptExportPercentage(20));

  const handledApiData = await worker(apiData, driverName);

  dispatch(saleSlice.actions.setSaleIrregularitiesReceiptExportPercentage(60));

  const zip = new JSZip();

  handledApiData.forEach(function (item) {
    zip.file(
      `${item.title}.html`,
      `
      <!doctype html>
      <html>
      <head>
        <meta name="viewport" content="width=device-width" />
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
        <title>Recibo de Irregularidades</title>
        <style>
          /* -------------------------------------
            GLOBAL RESETS
          ------------------------------------- */
          img {
            border: none;
            -ms-interpolation-mode: bicubic;
            max-width: 100%;
          }

          body {
            background-color: white;
            font-family: 'Arial', sans-serif;
            -webkit-font-smoothing: antialiased;
            font-size: 14px;
            line-height: 1.4;
            margin: 0;
            padding: 0;
            -ms-text-size-adjust: 100%;
            -webkit-text-size-adjust: 100%;
          }

          table {
            border-collapse: separate;
            mso-table-lspace: 0pt;
            mso-table-rspace: 0pt;
            width: 100%;
          }

          table td {
            font-family: 'Arial', sans-serif;
            font-size: 14px;
            vertical-align: top;
          }

          /* -------------------------------------
            BODY & CONTAINER
          ------------------------------------- */
          .body {
            background-color: white;
            width: 100%;
          }

          .container {
            display: block;
            Margin: 0 auto !important;
            max-width: 800px;
            padding: 20px;
            width: 800px;
          }

          .content {
            box-sizing: border-box;
            display: block;
            Margin: 0 auto;
            max-width: 800px;
            padding: 20px;
          }

          /* -------------------------------------
            HEADER, FOOTER, MAIN
          ------------------------------------- */
          .main {
            background: #fff;
            border-radius: 3px;
            width: 100%;
            border: 1px solid #ddd;
          }

          .wrapper {
            box-sizing: border-box;
            padding: 40px;
          }

          .footer {
            clear: both;
            padding-top: 20px;
            text-align: center;
            width: 100%;
            border-top: 1px solid #ddd;
            margin-top: 30px;
          }

          .footer td,
          .footer p,
          .footer span,
          .footer a {
            color: #666666;
            font-size: 12px;
            text-align: center;
          }

          /* -------------------------------------
            TYPOGRAPHY
          ------------------------------------- */
          h1,
          h2,
          h3,
          h4 {
            color: #000000;
            font-family: 'Arial', sans-serif;
            font-weight: 600;
            line-height: 1.4;
            margin: 0;
            Margin-bottom: 15px;
          }

          h1 {
            font-size: 24px;
            font-weight: 600;
            text-align: center;
            line-height: 1.4;
            margin: 0;
            Margin-bottom: 20px;
          }

          h3 {
            font-size: 16px;
            font-weight: 600;
          }

          p,
          ul,
          ol {
            font-family: 'Arial', sans-serif;
            font-size: 14px;
            font-weight: normal;
            margin: 0;
            Margin-bottom: 15px;
          }

          p li,
          ul li,
          ol li {
            list-style-position: inside;
            margin-left: 5px;
          }

          a {
            color: #3498db;
            text-decoration: underline;
          }

                     /* -------------------------------------
             RECEIPT SPECIFIC STYLES
           ------------------------------------- */
           .receipt-header {
             text-align: center;
             margin-bottom: 30px;
           }

           .receipt-title {
             font-size: 20px;
             font-weight: bold;
             margin-bottom: 10px;
           }

           .receipt-number {
             font-size: 16px;
             color: #666;
             text-align: right;
           }

           .receipt-content {
             margin-bottom: 30px;
             line-height: 1.6;
           }

           .receipt-content p {
             margin-bottom: 20px;
             text-align: justify;
           }

           .receipt-signature {
             margin-top: 40px;
           }

           .signature-section {
             margin-top: 30px;
           }

           .signature-line {
             border-top: 1px solid #000;
             width: 200px;
             margin: 10px 0;
           }

           .company-info {
             margin-top: 10px;
             font-size: 12px;
           }

          /* -------------------------------------
            RESPONSIVE AND MOBILE FRIENDLY STYLES
          ------------------------------------- */
          @media only screen and (max-width: 620px) {
            table[class=body] h1 {
              font-size: 20px !important;
              margin-bottom: 10px !important;
            }

            table[class=body] p,
            table[class=body] ul,
            table[class=body] ol,
            table[class=body] td,
            table[class=body] span,
            table[class=body] a {
              font-size: 14px !important;
            }

            table[class=body] .wrapper,
            table[class=body] .article {
              padding: 20px !important;
            }

            table[class=body] .content {
              padding: 0 !important;
            }

            table[class=body] .container {
              padding: 0 !important;
              width: 100% !important;
            }

            table[class=body] .main {
              border-left-width: 0 !important;
              border-radius: 0 !important;
              border-right-width: 0 !important;
            }
          }

          /* -------------------------------------
            PRESERVE THESE STYLES IN THE HEAD
          ------------------------------------- */
          @media all {
            .ExternalClass {
              width: 100%;
            }

            .ExternalClass,
            .ExternalClass p,
            .ExternalClass span,
            .ExternalClass font,
            .ExternalClass td,
            .ExternalClass div {
              line-height: 100%;
            }

            .apple-link a {
              color: inherit !important;
              font-family: inherit !important;
              font-size: inherit !important;
              font-weight: inherit !important;
              line-height: inherit !important;
              text-decoration: none !important;
            }
          }
        </style>
      </head>
      <body class="">
        <table border="0" cellpadding="0" cellspacing="0" class="body">
          <tr>
            <td>&nbsp;</td>
            <td class="container">
              <div class="content">
                <!-- Logo da niterói rotativo -->
                <div class="img-container" style="text-align:center; margin-bottom: 20px;">
                  <img src=${logoUrl} alt="Logo" height="66" width="100" />
                </div>
                <!-- START CENTERED WHITE CONTAINER -->
                <table class="main">
                  <!-- START MAIN CONTENT AREA -->
                  ${item.body}
                  <!-- END MAIN CONTENT AREA -->
                </table>
                <!-- END CENTERED WHITE CONTAINER -->
              </div>
            </td>
            <td>&nbsp;</td>
          </tr>
        </table>
      </body>
      </html>
      `,
    );
  });

  dispatch(saleSlice.actions.setSaleIrregularitiesReceiptExportPercentage(90));
  await delay(100);

  const content = await zip.generateAsync({ type: 'blob' });

  const now = new Date();
  const timestamp =
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0') +
    '_' +
    String(now.getHours()).padStart(2, '0') +
    '-' +
    String(now.getMinutes()).padStart(2, '0');

  FileSaver.saveAs(content, `recibos-irregularidades-${timestamp}.zip`);
};

export const handleApiDataIrregularitiesReceiptToPDF = (
  apiData: SalesIrregularitiesReceiptResponseType,
  driverName: string,
) => {
  const receipts: {
    title: string;
    body: string;
  }[] = [];

  apiData.forEach((item, index) => {
    const receiptNumber = item.id;
    const plate = item.plate;

    // Format date using native JavaScript
    const date = new Date(item.registerDateTime);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const registerDateTime = `${day}/${month}/${year}, às ${hours}:${minutes}h`;

    const address = item.address || 'Não informado';
    const price = item.price.toFixed(2).replace('.', ',');

    const receiptBody = `
      <div class="wrapper">
        <div class="receipt-header">
          <div class="receipt-title">RECIBO DE PRESTAÇÃO DE SERVIÇOS</div>
          <div class="receipt-number">${receiptNumber}</div>
        </div>

        <div class="receipt-content">
          <p>
            NITERÓI PARK LTDA, empresa constituída a partir do consórcio vencedor do certame licitatório, concernente ao Edital nº 06/98 e ao contrato administrativo de nº 03/99 firmado com o Município de Niterói, inscrita no CNPJ sob o nº 03.072.248/0001-56, com sede à Rua Dr. Paulo César nº 28, Icaraí, Cep.: 24.220-400, declara para os devidos fins que recebeu do Sr. <strong>${driverName}</strong>, a importância de <strong>R$ ${price}</strong> pela utilização do sistema de estacionamento rotativo de Niterói pelo veículo de placa <strong>${plate}</strong>, do dia <strong>${registerDateTime}</strong>, na <strong>${address}</strong>.
          </p>

          <p>Na oportunidade, renovamos protesto de estima e consideração.</p>

          <div class="receipt-signature">
            <div class="signature-section">
              <p>Niterói</p>
              <div class="signature-line"></div>
              <div class="company-info">
                <strong>NITERÓI PARK LTDA</strong><br>
                <small>CNPJ: 03.072.248/0001-56</small>
              </div>
            </div>
          </div>
        </div>

        <div class="footer">
          <p>
            Rua Dr Paulo César, nº 28, Icaraí, Cep.: 24.220-400, Niterói/RJ<br>
            Tel: (21) 2621-4839/ 2621-4797<br>
            Email: niteroirotativo@niteroirotativo.com.br
          </p>
        </div>
      </div>
    `;

    receipts.push({
      title: `recibo-irregularidade-${plate}-${receiptNumber}`,
      body: receiptBody,
    });
  });

  return receipts;
};